import Box from "@mui/material/Box";
import type { PaletteMode } from "@mui/material";
import { useEffect, useRef } from "react";

interface AmbientParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
}

const PARTICLE_COUNT = 42;

interface LandingBackgroundCanvasProps {
  mode: PaletteMode;
}

export default function LandingBackgroundCanvas({
  mode,
}: LandingBackgroundCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || process.env.NODE_ENV === "test") {
      return undefined;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }

    const ctx = context;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = window.innerWidth;
    let height = window.innerHeight;
    let frameId = 0;
    let scrollProgress = 0;
    const particles: AmbientParticle[] = [];

    const pointer = {
      x: width * 0.7,
      y: height * 0.25,
      active: false,
    };

    const updateScrollProgress = () => {
      const maxScroll = Math.max(
        document.body.scrollHeight - window.innerHeight,
        window.innerHeight
      );
      scrollProgress = Math.min(window.scrollY / maxScroll, 1);
    };

    const resetParticles = () => {
      particles.length = 0;
      for (let index = 0; index < PARTICLE_COUNT; index += 1) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.28,
          vy: (Math.random() - 0.5) * 0.28,
          radius: Math.random() * 2.1 + 1,
        });
      }
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;

      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

      resetParticles();
    };

    const drawBackdrop = (time: number) => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle =
        mode === "light"
          ? "rgba(246, 250, 255, 0.86)"
          : "rgba(7, 13, 20, 0.92)";
      ctx.fillRect(0, 0, width, height);

      const wave = (Math.sin(time * 0.00045) + 1) * 0.5;
      const centerX = width * (0.15 + wave * 0.18 + scrollProgress * 0.2);
      const centerY = height * 0.18;
      const glowMain = ctx.createRadialGradient(
        centerX,
        centerY,
        40,
        centerX,
        centerY,
        width * 0.54
      );
      glowMain.addColorStop(
        0,
        mode === "light"
          ? "rgba(74, 185, 255, 0.24)"
          : "rgba(21, 105, 170, 0.26)"
      );
      glowMain.addColorStop(1, "rgba(74, 185, 255, 0)");
      ctx.fillStyle = glowMain;
      ctx.fillRect(0, 0, width, height);

      const accentX = pointer.active
        ? pointer.x
        : width * (0.8 - scrollProgress * 0.24);
      const accentY = pointer.active
        ? pointer.y
        : height * (0.4 + scrollProgress * 0.18);
      const accentOuterRadius =
        mode === "dark" ? width * (pointer.active ? 0.2 : 0.24) : width * 0.42;
      const glowAccent = ctx.createRadialGradient(
        accentX,
        accentY,
        mode === "dark" ? 12 : 20,
        accentX,
        accentY,
        accentOuterRadius
      );
      glowAccent.addColorStop(
        0,
        mode === "light"
          ? "rgba(95, 187, 255, 0.18)"
          : pointer.active
          ? "rgba(72, 149, 209, 0.11)"
          : "rgba(72, 149, 209, 0.08)"
      );
      glowAccent.addColorStop(1, "rgba(95, 187, 255, 0)");
      ctx.fillStyle = glowAccent;
      ctx.fillRect(0, 0, width, height);
    };

    const updateParticles = () => {
      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < -10 || particle.x > width + 10) {
          particle.vx *= -1;
        }

        if (particle.y < -10 || particle.y > height + 10) {
          particle.vy *= -1;
        }
      });
    };

    const drawParticles = () => {
      const linkDistance = 160;
      for (let i = 0; i < particles.length; i += 1) {
        const first = particles[i];
        for (let j = i + 1; j < particles.length; j += 1) {
          const second = particles[j];
          const dx = first.x - second.x;
          const dy = first.y - second.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < linkDistance) {
            const alpha = (1 - distance / linkDistance) * 0.11;
            ctx.strokeStyle =
              mode === "light"
                ? `rgba(49, 141, 220, ${alpha})`
                : `rgba(82, 164, 228, ${alpha * 1.15})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(first.x, first.y);
            ctx.lineTo(second.x, second.y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((particle) => {
        ctx.fillStyle =
          mode === "light"
            ? "rgba(56, 156, 234, 0.42)"
            : "rgba(74, 170, 235, 0.46)";
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const drawFrame = (time: number) => {
      drawBackdrop(time);
      updateParticles();
      drawParticles();
      frameId = window.requestAnimationFrame(drawFrame);
    };

    const handlePointerMove = (event: MouseEvent) => {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.active = true;
    };

    const handlePointerLeave = () => {
      pointer.active = false;
    };

    resize();
    updateScrollProgress();

    window.addEventListener("resize", resize);
    window.addEventListener("scroll", updateScrollProgress, { passive: true });
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseout", handlePointerLeave);

    if (prefersReducedMotion) {
      drawBackdrop(0);
      drawParticles();
    } else {
      frameId = window.requestAnimationFrame(drawFrame);
    }

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", updateScrollProgress);
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseout", handlePointerLeave);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [mode]);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} />
    </Box>
  );
}
